import { useEffect, useState } from 'react';
import { LogWarning } from 'utils/logging';

/**
 * @summary use this when we want to know if an element is being viewed
 * @param {Object} ref - ref to DOM element
 * @param {*} rootMargin - when should we consider this element to be on the screen
 */
export default function useOnScreen(ref, rootMargin = '0px', options = {}) {
  const [state, setState] = useState({
    /* True if the element is on the screen given the rootMargin threshold */
    isOnScreen: !!options.isOnScreen,
    /* if isOnScreen is true on load */
    wasOnScreenOnLoad: null,
    /* if the element was above the vertical fold on load and horizontal fold on load */
    wasWithinViewPortOnLoad: null,
    /* similar to an isDirty flag */
    hasBeenSeen: false,
  });

  useEffect(() => {
    if (ref.current) {
      const currentRect = ref.current.getBoundingClientRect();

      setState((prevState) => ({
        ...prevState,
        wasWithinViewPortOnLoad:
          currentRect.top < window.innerHeight &&
          currentRect.left < window.innerWidth,
      }));
    }

    const observerOptions = {
      rootMargin,
    };

    let observer;
    const callback = ([entry]) => {
      setState((prevState) => ({
        ...prevState,
        isOnScreen: entry.isIntersecting,
        hasBeenSeen: prevState.hasBeenSeen || entry.isIntersecting,
        wasOnScreenOnLoad:
          prevState.wasOnScreenOnLoad === null
            ? entry.isIntersecting
            : prevState.wasOnScreenOnLoad,
      }));
    };

    try {
      observer = new IntersectionObserver(callback, observerOptions);

      if (ref.current) {
        observer.observe(ref.current);
      }
    } catch (error) {
      LogWarning('useOnScreen: Error observing element', {
        description: error.message,
      });
    }

    return () => {
      if (ref.current && observer) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref]);

  return state;
}
